import { action, makeObservable, observable, override, reaction } from 'mobx';

import { Tab } from '../configurator/Tab';
import { FinalVisualizationBuilder } from '../configurator/VisualizationBuilder';
import { IApiClient } from '../data/client';
import {
  BaseModelLiteData,
  GetProductFamiliesQuery,
  GetProductFamilyFiltersQuery,
  ImageData,
  ModularLayoutBasicData,
  PredefinedConfigurationData,
  ProductConfigurationMode,
  ProductFamilyBasicData,
  PropertyData,
  ShoppingContext,
} from '../data/model';
import type {
  GetProductFamiliesQueryResponse,
  GetProductFamilyFiltersQueryResponse,
  GetStoreContextQueryResponse,
} from '../data/model';
import { ConfiguratorUrlBuilder } from '../errorHandling/ParseConfigurationCodeAndRedirectState';
import { CatalogPageTranslation } from '../localization/SiteTranslation';
import ModularConfiguratorPageQuery from '../modularConfigurator/ModularConfiguratorPageQuery';
import { BasePageState } from '../shared/BasePageState';
import { AsyncCommand, debounce, PageMetadata } from '../shared/common';
import { PaginationState } from '../shared/components/Pagination/PaginationState';
import { IEventAggregator } from '../shared/EventAggregator';
import { TextInput } from '../shared/Form';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { INavigationService } from '../shared/NavigationService';
import { StoreUrl } from '../shared/StoreUrl';
import { ICancellableTask, RunLastTasksScheduler } from '../shared/TasksScheduler';
import { RelativeUrl } from '../shared/Url';
import { VisualizationMode } from '../shared/visualization';
import { StoreState } from '../StoreState';
import { CatalogFiltersState } from './CatalogFiltersState';
import { ProductListViewedEvent, ProductSelectedEvent } from './CatalogPageEvents';
import { CatalogPageQueryConverter } from './CatalogPageQueryConverter';
import { CatalogPageQueryParser, ICatalogPageQuery } from './CatalogPageQueryParser';

interface ICatalogMemento {
  getStoreContextQueryResponse: GetStoreContextQueryResponse;
  getProductFamilyFiltersQueryResponse: GetProductFamilyFiltersQueryResponse;
  getProductFamiliesQueryResponse: GetProductFamiliesQueryResponse;
}

export interface IFamilyItemState {
  id: string;
  imageUrl: string;
  isConfiguratorAvailable: boolean;
  canBePurchased: boolean;
  name: string;
  url: RelativeUrl;
  goToConfigurator: () => void;
}

export class BaseModelState implements IFamilyItemState {
  public brand: string;
  public imageUrl: string;
  public url: RelativeUrl;
  public id: string;
  public name: string;
  public predefinedConfiguration: PredefinedConfigurationData;
  public canBePurchased: boolean;
  public visualizationAspectRatio: string;
  public readonly data: BaseModelLiteData;

  public client: IApiClient;
  public navigation: INavigationService;

  public goToConfiguratorCommand: AsyncCommand;

  public get configurationCode() {
    return this.predefinedConfiguration?.code;
  }

  public get isConfiguratorAvailable() {
    return !!this.configurationCode;
  }

  constructor(
    familyData: ProductFamilyBasicData,
    data: BaseModelLiteData,
    client: IApiClient,
    navigation: INavigationService,
  ) {
    this.client = client;
    this.navigation = navigation;

    this.data = data;
    this.brand = familyData.brand;
    this.imageUrl = BaseModelState.getImageUrl(data);
    this.url = StoreUrl.productPageUrl(familyData.id, data.id, this.configurationCode);
    this.id = data.id;
    this.name = data.name;
    this.canBePurchased = data.canBePurchased;
    this.predefinedConfiguration = data.predefinedConfiguration;
    this.visualizationAspectRatio = data.visualizationAspectRatio;
    this.goToConfiguratorCommand = new AsyncCommand(() => this.goToConfigurator());
  }

  async goToConfigurator(tab: Tab = Tab.Fabrics): Promise<void> {
    const builder = new ConfiguratorUrlBuilder(this.client);
    const configuratorUrl = await builder.buildFromCode(this.predefinedConfiguration.code, undefined, tab);
    this.navigation.navigateTo(configuratorUrl);

    return Promise.resolve();
  }

  private static getImageUrl(data: BaseModelLiteData) {
    if (data.visualizationMode === VisualizationMode.Default || !data.image) {
      return FinalVisualizationBuilder.buildUrlForPredefinedConfiguration(data.predefinedConfiguration, data.image);
    }

    return data.image.url;
  }
}

export class LayoutState implements IFamilyItemState {
  public canBePurchased = true;
  public id: string;
  public imageUrl: string;
  public isConfiguratorAvailable = true;
  public name: string;
  public url: RelativeUrl;

  private readonly configuratorUrl: RelativeUrl;

  public constructor(
    private readonly navigation: INavigationService,
    familyData: ProductFamilyBasicData,
    data: ModularLayoutBasicData,
  ) {
    this.name = data.name;
    this.id = data.id;
    this.imageUrl = data.thumbnail.url;
    this.configuratorUrl = StoreUrl.modularConfiguratorUrl(
      familyData.id,
      new ModularConfiguratorPageQuery(data.code, ''),
    );
  }

  public goToConfigurator() {
    this.navigation.navigateTo(this.configuratorUrl);
  }
}

export class ProductFamilyState {
  public readonly data: ProductFamilyBasicData;
  public brand: string;
  public name: string;
  public shortName: string;
  public url: RelativeUrl;
  public imageUrl: string;
  public models: Array<BaseModelState> = [];
  public properties: Array<PropertyData> = [];
  public listingIcon?: ImageData;
  public layouts: Array<LayoutState> = [];
  public configuratorUrl?: RelativeUrl;
  public readonly configurationCode?: string;

  constructor(data: ProductFamilyBasicData, client: IApiClient, navigation: INavigationService) {
    this.data = data;
    this.brand = data.brand;
    this.name = data.name;
    this.shortName = data.shortName;
    this.url = StoreUrl.productPageUrl(data.id);
    this.models = data.models.map((x) => new BaseModelState(data, x, client, navigation)).filter((x) => x.imageUrl);
    this.properties = data.properties;
    this.listingIcon = data.listingIcon;
    this.layouts = data.layouts?.map((x) => new LayoutState(navigation, data, x)) ?? [];
    this.configuratorUrl = this.getConfiguratorUrl(data.configurationMode, data.id);

    if (data.image.url) {
      this.imageUrl = data.image.url;
    } else if (this.models.any()) {
      const firstModel = this.models.first();
      this.configurationCode = firstModel.configurationCode;
      this.imageUrl = firstModel.imageUrl;
      this.url = firstModel.url;
    }
  }

  private getConfiguratorUrl(configurationMode: ProductConfigurationMode, productFamilyId: string): RelativeUrl {
    switch (configurationMode) {
      case ProductConfigurationMode.Modular:
        return StoreUrl.modularConfiguratorUrl(productFamilyId, new ModularConfiguratorPageQuery('', ''));
      case ProductConfigurationMode.Default:
      default:
        return undefined;
    }
  }
}

class LoadFamiliesTask implements ICancellableTask {
  public cancelled = false;
  public query: GetProductFamiliesQuery;

  public constructor(query: GetProductFamiliesQuery) {
    this.query = query;
  }
}

export class CatalogPageState extends BasePageState<ICatalogMemento> {
  @observable.ref
  public families: ProductFamilyState[];
  public filters: CatalogFiltersState;
  public loadingIndicator: LoadingIndicator;
  public pagination: PaginationState;
  public searchInput: TextInput;
  public urlQuery: ICatalogPageQuery;

  private getStoreContextQueryResponse: GetStoreContextQueryResponse;
  private getProductFamilyFiltersQueryResponse: GetProductFamilyFiltersQueryResponse;
  private getProductFamiliesQueryResponse: GetProductFamiliesQueryResponse;
  private loadFamiliesScheduler: RunLastTasksScheduler<LoadFamiliesTask, void>;

  public constructor(
    public readonly translation: CatalogPageTranslation,
    private readonly navigation: INavigationService,
    private readonly client: IApiClient,
    private readonly eventAggregator: IEventAggregator,
    pageSize: number = 12,
  ) {
    super(translation);
    makeObservable(this);
    this.loadFamiliesScheduler = new RunLastTasksScheduler(this.runLoadFamiliesTask);
    this.loadingIndicator = new LoadingIndicator();
    this.searchInput = new TextInput();
    this.urlQuery = CatalogPageQueryParser.toModel(this.navigation.currentUrl.query);
    this.initializePagination(pageSize);

    this.onPageLoaded = this.onPageLoaded.bind(this);
    this.navigation.pageLoaded.subscribe(this.onPageLoaded);
  }

  public get counter(): number {
    return this.pagination.totalCount;
  }

  public getMemento(): ICatalogMemento {
    return {
      getStoreContextQueryResponse: this.getStoreContextQueryResponse,
      getProductFamilyFiltersQueryResponse: this.getProductFamilyFiltersQueryResponse,
      getProductFamiliesQueryResponse: this.getProductFamiliesQueryResponse,
    };
  }

  @override
  public get metadata() {
    const title = this.translations.pageTitleFormat;
    const description = this.translations.pageDescriptionFormat;

    return <PageMetadata>{
      title: title,
      description: description,
    };
  }

  @action
  public async onLoad(store: StoreState) {
    const getProductFamilyFiltersQueryResponse = await this.client.send(
      new GetProductFamilyFiltersQuery({ shoppingContext: store.shoppingContext }),
    );
    const getProductFamiliesQueryResponse = await this.client.send(
      this.createInitialProductFamiliesQuery(store.shoppingContext),
    );
    this.initialize(store.storeResponse, getProductFamilyFiltersQueryResponse, getProductFamiliesQueryResponse);
  }

  public restoreMemento(memento: ICatalogMemento) {
    this.initialize(
      memento.getStoreContextQueryResponse,
      memento.getProductFamilyFiltersQueryResponse,
      memento.getProductFamiliesQueryResponse,
    );
  }

  public unload() {
    this.navigation.pageLoaded.unsubscribe(this.onPageLoaded);
  }

  public searchFamilies = () => {
    this.pagination.setCurrentPageIndex(0, true);
    this.loadFamilies();
  };

  public notifyProductSelected = (family: ProductFamilyState, index: number, model?: BaseModelState) => {
    this.eventAggregator.publish(
      new ProductSelectedEvent(
        this.getStoreContextQueryResponse.storeContext,
        { family: family.data, model: (model ?? family.models[0])?.data },
        this.pagination.startIndex,
        index,
      ),
    );
  };

  private onPageLoaded() {
    if (this.families.length > 0) {
      this.notifyProductListViewed();
    }
  }

  private notifyProductListViewed() {
    this.eventAggregator.publish(
      new ProductListViewedEvent(
        this.getStoreContextQueryResponse.storeContext,
        // The first model is taken since it represents the family on the catalog page
        this.families.map((family) => ({ family: family.data, model: family.models[0]?.data })),
        this.pagination.startIndex,
      ),
    );
  }

  private createInitialProductFamiliesQuery(shoppingContext: ShoppingContext): GetProductFamiliesQuery {
    return new GetProductFamiliesQuery({
      shoppingContext,
      types: this.urlQuery.type ?? [],
      sectors: this.urlQuery.sector ?? [],
      brands: this.urlQuery.brand ?? [],
      searchPhrase: this.urlQuery.phrase,
      skip: this.pagination.startIndex,
      take: this.pagination.pageSize,
    });
  }

  private initialize(
    getStoreContextQueryResponse: GetStoreContextQueryResponse,
    getProductFamilyFiltersQueryResponse: GetProductFamilyFiltersQueryResponse,
    getProductFamiliesQueryResponse: GetProductFamiliesQueryResponse,
  ) {
    this.getStoreContextQueryResponse = getStoreContextQueryResponse;
    this.getProductFamilyFiltersQueryResponse = getProductFamilyFiltersQueryResponse;
    this.getProductFamiliesQueryResponse = getProductFamiliesQueryResponse;
    this.filters = new CatalogFiltersState(getProductFamilyFiltersQueryResponse, this.urlQuery);

    const converter = new CatalogPageQueryConverter(this.filters, this.pagination, this.searchInput, this.navigation);
    converter.initialize();

    this.setFamilies(getProductFamiliesQueryResponse);
    this.pagination.setTotalCount(getProductFamiliesQueryResponse.totalCount);

    reaction(
      () => this.filters.selection.properties.slice(),
      () => this.searchFamilies(),
    );
    reaction(
      () => this.searchInput.value,
      debounce(() => {
        this.searchFamilies();
        converter.updateQuery();
      }, 300),
    );
  }

  private initializePagination(pageSize: number) {
    this.pagination = new PaginationState(pageSize);

    const currentPageIndex = this.urlQuery.page - 1;
    if (currentPageIndex >= 0) {
      this.pagination.setCurrentPageIndex(this.urlQuery.page - 1);
    }

    this.pagination.pageChanged.subscribe(async () => {
      await this.loadFamilies();
      this.notifyProductListViewed();
    });
  }

  private loadFamilies = () => {
    const query = new GetProductFamiliesQuery({
      shoppingContext: this.getStoreContextQueryResponse.shoppingContext,
      types: this.filters.selectedTypes,
      sectors: this.filters.selectedSectors,
      brands: this.filters.selectedBrands,
      searchPhrase: this.searchInput.value,
      skip: this.pagination.startIndex,
      take: this.pagination.pageSize,
    });

    return this.loadFamiliesScheduler.run(new LoadFamiliesTask(query));
  };

  private runLoadFamiliesTask = async (task: LoadFamiliesTask) => {
    this.loadingIndicator.start();

    const response = await this.client.send(task.query);

    if (task.cancelled) {
      return;
    }

    this.setFamilies(response);
    this.pagination.setTotalCount(response.totalCount);

    this.loadingIndicator.reset();
  };

  @action.bound
  private setFamilies(response: GetProductFamiliesQueryResponse) {
    if (!response || !response.productFamilies || response.productFamilies.empty()) {
      this.families = [];
      return;
    }

    this.families = response.productFamilies.map((x) => new ProductFamilyState(x, this.client, this.navigation));
  }
}
